import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import {
  Autocomplete,
  CircularProgress,
  Paper,
  Popper,
  TextField,
} from "@mui/material";
import { FONT_FAMILY } from "@/utils/font-family";
import { ClickOutside } from "@/components/click-out-side/click-out-side";
import { RouteChangeConfirmation } from "@/components/handle-navigation/handle-navigation";
import { ArrowUpIcon } from "@/icons";
import { useRef } from "react";
import { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import React from "react";

interface IUpdateValueAutoCompleteProps {
  value?: any;
  onUpdate?: () => void;
  onCancel?: () => void;
  onInputChange?: (e: string) => void;
  clickedOut?: () => void;
  options?: any;
  onChange?: any;
  getOptionLabel?: any;
  onChangeTextField?: any;
  loading?: boolean;
  defaultFiltering?: boolean;
  renderOption?: any;
  filterOptions?: (options: any[], state: { inputValue: string }) => any[];
  /** Show the current option in the field. Used by the modern quote settings menu. */
  selectValue?: boolean;
}

/** Modern quote settings: list width matches the bordered field, not the inner input. */
const QuoteSettingsSelectPopper = (props: React.ComponentProps<typeof Popper>) => {
  const { t } = useTranslation();
  const RTL = t("direction") === "rtl";
  const anchorEl = props.anchorEl as HTMLElement | null;
  const fieldRoot = anchorEl?.closest(
    ".gm-quote-settings-select, .gm-pricing-supplier-select"
  ) as HTMLElement | null;
  const fieldEl = (fieldRoot?.querySelector(
    ".MuiPaper-root:has(.MuiAutocomplete-root)"
  ) || fieldRoot) as HTMLElement | null;
  const fieldWidth = fieldEl?.getBoundingClientRect().width;

  return (
    <Popper
      {...props}
      placement={RTL ? "bottom-end" : "bottom-start"}
      className={
        props.className
          ? `${props.className} gm-quote-settings-autocomplete-popper`
          : "gm-quote-settings-autocomplete-popper"
      }
      style={{
        ...props.style,
        zIndex: 1600,
        ...(fieldWidth ? { width: `${fieldWidth}px` } : {}),
      }}
    />
  );
};

const UpdateValueAutoComplete = ({
  clickedOut,
  options,
  onChange,
  getOptionLabel,
  onChangeTextField,
  loading = false,
  defaultFiltering = true,
  renderOption,
  value,
  filterOptions,
  selectValue = false,
}: IUpdateValueAutoCompleteProps) => {
  const { secondColor } = useGomakeTheme();
  const { t } = useTranslation();
  const optionsListEl = useRef<HTMLDivElement | null>(null);
  const dir: "rtl" | "ltr" = t("direction");
  const RTL = dir === "rtl";
  const optionLabel = (option: any) => {
    if (option == null) return "";
    if (!getOptionLabel) {
      return String(option?.text ?? option?.label ?? option ?? "");
    }
    try {
      return String(getOptionLabel(option) ?? "");
    } catch {
      return "";
    }
  };
  const matchedOption = selectValue
    ? (options || []).find((option) => optionLabel(option) === String(value ?? "")) ??
    null
    : undefined;
  const GlobalStyle = createGlobalStyle`
  .MuiAutocomplete-input {
    padding-left:0px !important;
  }
    .MuiAutocomplete-endAdornment {
    top: 50% !important;
    transform: translateY(-50%);
  }
`;
  return (
    <>
      <GlobalStyle />
      <ClickOutside exceptionRef={optionsListEl} onClick={clickedOut}>
        <RouteChangeConfirmation />
        <Paper
          component="form"
          sx={{
            paddingLeft: selectValue ? "0px" : "4px",
            paddingRight: selectValue ? "0px" : "2px",
            display: "inline-flex",
            width: selectValue ? "100%" : 250,
            height: selectValue ? "36px" : "26px",
            ...(selectValue
              ? { border: "none", boxShadow: "none" }
              : {
                  border: "1px solid " + secondColor(500),
                  boxShadow: "none",
                }),
            position: "relative",
          }}
        >
          <Autocomplete
            id="updateValueAutoComplete"
            options={options}
            {...(selectValue ? { PopperComponent: QuoteSettingsSelectPopper } : {})}
            sx={{
              ml: selectValue ? 0 : 1,
              flex: 1,
              ...FONT_FAMILY.Lexend(400, 14),
              padding: 0,
              position: "relative",
              width: selectValue ? "100%" : undefined,
            }}
            getOptionLabel={selectValue ? optionLabel : getOptionLabel}
            {...(selectValue
              ? {
                value: matchedOption,
                openOnFocus: true,
                isOptionEqualToValue: (option: any, selected: any) =>
                  option === selected ||
                  (option?.value != null &&
                    option?.value === selected?.value) ||
                  optionLabel(option) === optionLabel(selected),
              }
              : {})}
            onChange={(e: any, value: any) => {
              onChange(e, value);
            }}
            renderOption={renderOption}
            renderInput={(params) => (
              <TextField
                {...params}
                {...(selectValue ? { autoFocus: true } : {})}
                onChange={onChangeTextField}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  ...(loading && {
                    endAdornment: (
                      <CircularProgress
                        color="inherit"
                        size={16}
                        sx={{ ml: 2 }}
                      />
                    ),
                  }),
                }}
              />
            )}
            loading={loading} // Pass loading prop to show spinner
            popupIcon={
              selectValue ? (
                <ArrowUpIcon stroke="var(--theme-text-primary, #1e1e24)" />
              ) : RTL ? (
                <div style={{ position: "absolute", left: -225 }}>
                  <ArrowUpIcon
                    stroke={`var(--theme-secondary-color, #ED028C)`}
                  />
                </div>
              ) : (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <ArrowUpIcon
                    stroke={`var(--theme-secondary-color, #ED028C)`}
                  />
                </div>
              )
            }
            style={{ height: 26 }}
            clearIcon={false}
            {...(selectValue
              ? {
                componentsProps: {
                  popper: { style: { zIndex: 1600 } },
                },
              }
              : {})}
            PaperComponent={React.useCallback(
              (props) => (
                <Paper
                  ref={optionsListEl}
                  {...props}
                  elevation={1}
                  sx={{
                    ...(props as any).sx,
                    zIndex: 1500, // Ensure proper stacking
                  }}
                >
                  {props.children}
                </Paper>
              ),
              []
            )}
            // filterOptions={defaultFiltering ? undefined : (x) => x}
            filterOptions={
              selectValue
                ? (opts, state) => {
                  const query = String(state.inputValue ?? "")
                    .trim()
                    .toLowerCase();
                  const selectedLabel = optionLabel(matchedOption)
                    .trim()
                    .toLowerCase();
                  if (!query || (selectedLabel && query === selectedLabel)) {
                    return opts;
                  }
                  return opts.filter((option) =>
                    optionLabel(option).toLowerCase().includes(query)
                  );
                }
                : filterOptions ?? (defaultFiltering ? undefined : (x) => x)
            }
          />
        </Paper>
      </ClickOutside>
    </>
  );
};

export { UpdateValueAutoComplete };
